import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import type { IssueListItem } from '../../services/issueService';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';

interface IssueCalendarViewProps {
  items: IssueListItem[];
  onIssueClick: (issue: IssueListItem) => void;
}

export default function IssueCalendarView({ items, onIssueClick }: IssueCalendarViewProps) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  const { weeks } = useMemo(() => {
    const dim = new Date(year, month + 1, 0).getDate();
    const fdow = new Date(year, month, 1).getDay();
    const wks: (number | null)[][] = [];
    let week: (number | null)[] = Array(fdow).fill(null);
    for (let d = 1; d <= dim; d++) {
      week.push(d);
      if (week.length === 7) {
        wks.push(week);
        week = [];
      }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      wks.push(week);
    }
    return { weeks: wks };
  }, [year, month]);

  // Group issues by due date day
  const issuesByDay = useMemo(() => {
    const map = new Map<number, IssueListItem[]>();
    items.forEach((issue) => {
      if (!issue.dueDate) return;
      const d = new Date(issue.dueDate);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!map.has(day)) map.set(day, []);
        map.get(day)!.push(issue);
      }
    });
    return map;
  }, [items, year, month]);

  const monthName = new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: 'long' }).format(
    new Date(year, month),
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">{monthName}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1.5 hover:bg-gray-100 rounded-lg" disabled>
            <ChevronLeft size={16} className="text-gray-400" />
          </button>
          <button className="p-1.5 hover:bg-gray-100 rounded-lg" disabled>
            <ChevronRight size={16} className="text-gray-400" />
          </button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
            <div key={day} className="px-2 py-2 text-sm font-medium text-gray-500 text-center">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-gray-100 last:border-0">
            {week.map((day, di) => {
              const dayIssues = day ? issuesByDay.get(day) ?? [] : [];
              const isToday = day === now.getDate();
              return (
                <div
                  key={di}
                  className={`min-h-[80px] px-1.5 py-1 border-r border-gray-100 last:border-0 ${
                    !day ? 'bg-gray-50/50' : ''
                  }`}
                >
                  {day && (
                    <>
                      <div
                        className={`text-sm font-medium mb-1 ${
                          isToday
                            ? 'w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center'
                            : 'text-gray-500'
                        }`}
                      >
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {dayIssues.slice(0, 2).map((issue) => (
                          <button
                            key={issue.id}
                            onClick={() => onIssueClick(issue)}
                            className="block w-full text-left px-1 py-0.5 text-[11px] rounded bg-primary-50 text-primary-700 hover:bg-primary-100 truncate"
                          >
                            {(() => {
                              const cfg = resolveIssueTypeConfig(issue.issueTypeDef, issue.issueType);
                              return <cfg.Icon size={10} className={`inline-block mr-0.5 ${cfg.color}`} style={cfg.hexColor ? { color: cfg.hexColor } : undefined} />;
                            })()}
                            {issue.issueId}
                          </button>
                        ))}
                        {dayIssues.length > 2 && (
                          <div className="text-[11px] text-gray-400 px-1">
                            +{dayIssues.length - 2}건
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-700">
          캘린더 뷰는 현재 월의 마감일 기준으로 작업을 표시합니다. 월 이동 기능은 후속 업데이트에서 지원 예정입니다.
        </p>
      </div>
    </div>
  );
}
